import { useState } from "react";
import { useParams } from "react-router-dom";
import { usePublicSellerListings } from "@/hooks/usePublicSellerListings";
import PublicHeader from "@/components/public/PublicHeader";
import PublicListingsHeader from "@/components/public/PublicListingsHeader";
import PublicPropertyGrid from "@/components/public/PublicPropertyGrid";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import PageSEO from "@/components/seo/PageSEO";
import { Loader2, Building2 } from "lucide-react";

const PublicListingsPage = () => {
  const { sellerId } = useParams<{ sellerId: string }>();
  const [statusFilter, setStatusFilter] = useState<'active' | 'pending'>('active');
  const {
    properties,
    seller,
    isLoading,
    error,
    totalCount,
    hasMore,
    loadMore,
    isLoadingMore
  } = usePublicSellerListings(sellerId || "", statusFilter);

  // Dynamic SEO based on seller data - use totalCount for accurate count
  const sellerName = seller?.business_name || seller?.full_name || "Seller";
  const pageTitle = `${sellerName}'s Properties | GoDyspo`;
  const pageDescription = `View investment properties from ${sellerName}. Browse ${totalCount || 0} deals and connect with verified sellers on GoDyspo.`;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PageSEO
          title="Loading Properties | GoDyspo"
          description="Loading seller properties on GoDyspo."
          noIndex={true}
        />
        <PublicHeader />
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-32 w-full mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-80 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    if (error === "authentication_required") {
      return (
        <div className="min-h-screen bg-background">
          <PageSEO
            title="Sign Up to View Properties | GoDyspo"
            description="Create your free GoDyspo account to access exclusive investment properties and connect with sellers."
            canonical={`/public/seller/${sellerId}/listings`}
          />
          <PublicHeader />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md mx-auto px-4">
              <h1 className="text-2xl font-bold mb-4">Sign Up to View Properties</h1>
              <p className="text-muted-foreground mb-6">
                This seller has exclusive investment properties available. 
                Create your free account to access detailed property information, 
                investment metrics, and connect with sellers.
              </p>
              <div className="space-y-4">
                <a 
                  href="/buyer/register" 
                  className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Create Free Account - View Properties
                </a>
                <p className="text-xs text-muted-foreground">
                  Free for buyers • No credit card required
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background">
        <PageSEO
          title="Unable to Load Listings | GoDyspo"
          description="We couldn't load the requested property listings."
          noIndex={true}
        />
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <h1 className="text-2xl font-bold mb-4">Unable to Load Listings</h1>
            <p className="text-muted-foreground mb-4">
              {error === "Seller not found" 
                ? "The seller you're looking for doesn't exist or doesn't have any active listings to share."
                : error
              }
            </p>
            <p className="text-sm text-muted-foreground">
              Please check the link and try again, or contact the seller directly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-background">
        <PageSEO
          title="Seller Not Found | GoDyspo"
          description="The seller you're looking for doesn't exist or has no active listings."
          noIndex={true}
        />
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Seller Not Found</h1>
            <p className="text-muted-foreground">
              The seller you're looking for doesn't exist or has no active listings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title={pageTitle}
        description={pageDescription}
        canonical={`/public/seller/${sellerId}/listings`}
      />
      <PublicHeader />
      <PublicListingsHeader seller={seller} propertyCount={totalCount} statusFilter={statusFilter} sellerParam={sellerId} />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 inline-flex rounded-md border border-border bg-card p-1">
          <Button
            size="sm"
            variant={statusFilter === 'active' ? 'default' : 'ghost'}
            onClick={() => setStatusFilter('active')}
          >
            Active
          </Button>
          <Button
            size="sm"
            variant={statusFilter === 'pending' ? 'default' : 'ghost'}
            onClick={() => setStatusFilter('pending')}
          >
            Pending
          </Button>
        </div>
        {properties.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-6">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">
              {statusFilter === 'active' ? 'No Active Properties' : 'No Pending Properties'}
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              {statusFilter === 'active'
                ? `${seller?.business_name || seller?.full_name} doesn't have any active listings right now. Check back soon!`
                : `${seller?.business_name || seller?.full_name} doesn't have any pending deals right now.`}
            </p>
          </div>
        ) : (
          <>
            <PublicPropertyGrid properties={properties} />
            
            {/* Load More Section */}
            <div className="mt-8 flex flex-col items-center gap-4">
              {totalCount > 0 && (
                <p className="text-sm text-muted-foreground">
                  Showing {properties.length} of {totalCount} {totalCount === 1 ? 'property' : 'properties'}
                </p>
              )}
              
              {hasMore && (
                <Button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  variant="outline"
                  className="gap-2"
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Building2 className="h-4 w-4" />
                      Load More Properties
                    </>
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicListingsPage;
